import { useState } from 'react';
import { AlertTriangle, Wifi, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useLicenseStore } from '@/stores/licenseStore';

interface GracePeriodWarningProps {
  open: boolean;
  onVerify: () => Promise<void>;
}

/**
 * Shown during grace period (last 3-7 days before blocking)
 * More urgent warning that app will stop working soon
 */
export function GracePeriodWarning({ open, onVerify }: GracePeriodWarningProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const { gracePeriodEndsAt, graceDaysRemaining, license } = useLicenseStore();

  const daysRemaining = graceDaysRemaining() || 0;
  const totalGraceDays = license?.grace_period_days || 7;
  const progressPercent = ((totalGraceDays - daysRemaining) / totalGraceDays) * 100;

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await onVerify();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Dialog open={open} modal>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-100 rounded-full animate-pulse">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <DialogTitle className="text-xl text-red-700">
              Action requise immédiatement
            </DialogTitle>
          </div>
          <DialogDescription className="text-right text-sm text-muted-foreground mt-2">
            الإجراء المطلوب على الفور
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Critical warning */}
          <Alert className="border-red-300 bg-red-50">
            <AlertDescription>
              <p className="font-bold text-red-900 text-lg mb-2">
                ⚠️ Période de grâce: {daysRemaining} jour(s) restant(s)
              </p>
              <p className="text-red-800">
                Votre licence n'a pas été vérifiée depuis plus de 30 jours. L'application cessera
                de fonctionner dans <span className="font-bold">{daysRemaining} jour(s)</span> si
                la vérification n'est pas effectuée.
              </p>
            </AlertDescription>
          </Alert>

          {/* Grace period progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Période de grâce</span>
              <div className="flex items-center gap-1 text-red-600 font-medium">
                <Clock className="w-3 h-3" />
                {daysRemaining}/{totalGraceDays} jours
              </div>
            </div>
            <Progress value={progressPercent} className="h-2 bg-red-100" />
          </div>

          {/* What happens next */}
          <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg space-y-2">
            <p className="text-sm font-medium text-gray-900">Que se passe-t-il ensuite ?</p>
            <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
              <li>Dans {daysRemaining} jour(s), l'application sera bloquée</li>
              <li>Vous ne pourrez plus accéder à vos données</li>
              <li>Les modifications en attente seront perdues</li>
              <li>
                La vérification nécessite une connexion Internet (quelques secondes seulement)
              </li>
            </ul>
          </div>

          {/* License info */}
          {license && license.license_key && (
            <div className="p-3 bg-muted rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Clé de licence:</span>
                <code className="text-xs font-mono bg-background px-2 py-1 rounded">
                  {license.license_key?.substring(0, 16)}...
                </code>
              </div>
              {gracePeriodEndsAt && (
                <div className="flex justify-between items-center text-red-700">
                  <span className="font-medium">Blocage le:</span>
                  <span className="font-bold">
                    {new Date(gracePeriodEndsAt).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Arabic translation */}
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-right">
            <p className="text-xs text-red-900 leading-relaxed">
              <strong>⚠️ تحذير عاجل</strong>
              <br />
              لم يتم التحقق من ترخيصك منذ أكثر من 30 يومًا. سيتوقف التطبيق عن العمل في غضون{' '}
              <strong>{daysRemaining} يوم (أيام)</strong> إذا لم يتم التحقق منه. يرجى الاتصال
              بالإنترنت والتحقق الآن.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleVerify}
            disabled={isVerifying}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
            size="lg"
          >
            {isVerifying ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Vérification en cours...
              </>
            ) : (
              <>
                <Wifi className="w-4 h-4 mr-2" />
                Vérifier maintenant (obligatoire)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
